import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import * as query from '../../../query'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

/**
 * Retrieves the result(s) of the given subquery
 *
 * @param alias Alias of the desired subquery
 * @example
 * // Retrieve an instance of the `myOwner` subquery which returns an object
 * // and is one-to-* with the query entity
 * { '@': 'myOwner' }
 * // ->
 * { 'ID': 'xyz123', 'Name': 'John Smith', ... }
 *
 * // Retrieve an instance of the `myOwnerId` subquery which returns a string
 * // and is one-to-* with the query entity
 * { '@': 'myOwnerId' }
 * // ->
 * 'xyz123'
 *
 * // Retrieve a list of instances of the `myDepartments` subquery which
 * // returns an object is many-to-* with the query entity
 * { '@': 'myDepartments' }
 * // ->
 * [
 *   { 'ID': 'chem', 'Name': 'Chemistry', ... },
 *   { 'ID': 'bio', 'Name': 'Biology', ... },
 *   ...
 * ]
 *
 * // Retrieve a list of instances of the `myDepartmentIds` subquery which
 * // returns a string is many-to-* with the query entity
 * { '@': 'myDepartmentIds' }
 * // ->
 * [ 'chem', 'bio', ... ]
 */
export interface GetSubquery<out T = any> {
  /** {@link GetSubquery} */
  readonly [Key.GetSubquery]: query.Subquery.Name
}
export namespace GetSubquery {
  export const isGetSubquery: TypeChecker<GetSubquery> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<GetSubquery>({
        [Key.GetSubquery]: query.Subquery.Name.isName,
      }),
    ).usingAlias('Fn.GetSubquery')
  export const assertIsGetSubquery: TypeAsserter<GetSubquery> =
    TypeAsserter.Factory.from(isGetSubquery)

  /**
   * @see {@link Node}
   */
  export class Node<T = any> extends fnNode.Node<
    T,
    Key.GetSubquery,
    query.graph.node.Node
  > {
    // NOTE: done as getter since this depends on edges which may not be
    // available initially
    public override get type(): common.value.types.Type | undefined {
      if (
        // In theory, the node should always pass these (at least as of
        // 5/24/2024), but doing the check narrows the types and adds a little
        // type safety
        !query.graph.node.ForGroup.isForGroup(this.arg) &&
        !query.graph.node.ForRecords.isForRecords(this.arg)
      ) {
        return undefined
      }
      // First, we need to find the edge that represents this consumption of
      // the subquery so that we can check resultsAsSingleton
      const consumptionEdge = this.arg.edges.consumedBy.edges.find(
        edge => edge.node.id === this._context.queryNodeId,
      )
      if (!consumptionEdge) {
        // We can only figure out the type if we were able to find the edge
        return undefined
      }

      let selectionType: common.value.types.Type | undefined
      // If the result is a singleton, we need to look at the node
      if (query.graph.node.ForRecords.isForRecords(this.arg)) {
        // If the node is for an entity, then we just use the selection
        // type
        selectionType = this.arg.selection.type
      } else {
        // If the node is for a group, then we need to check all the types
        // that appear in that group
        const possibleTypes = new Set(
          this.arg.members
            .filter(query.graph.node.ForRecords.isForRecords)
            .map(member => member.selection.type),
        )
        if (possibleTypes.size === 1) {
          // If all the nodes in the group return the same type, we can
          // use that type
          selectionType = possibleTypes.values().next().value
        }
      }
      // If the result is a singleton, then we just return its selection type.
      // Otherwise, it is a list of its selection type
      return consumptionEdge.resultsAsSingleton
        ? selectionType
        : selectionType
          ? common.value.types.Structure.list(selectionType)
          : common.value.types.Intrinsic.List
    }

    constructor(
      arg: Node<T>['arg'],
      private readonly _context: Readonly.Deep<expressionNode.Context>,
    ) {
      super(Key.GetSubquery, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<GetSubquery> = {
    createNode: (fn, context) => {
      const subqueryNode = context.subqueryNodes[fn[Key.GetSubquery]]
      // If subquery node already has a value, use that value, otherwise just
      // reference the subquery node
      return subqueryNode instanceof query.graph.node.ForValue
        ? new expressionNode.ForValue(subqueryNode.value)
        : new Node(
            TypeAsserter.validate(
              subqueryNode,
              TypeAsserter.assertIsNotNullish,
            ),
            context,
          )
    },
    expressionsInArgs: fn => [],
    typeChecker: isGetSubquery,
  }
}
